// #region Imports
import { useMinuteClock } from "_shared/dates/use-minute-clock";
import { traverserStopCellTint, traverserStopRowBorderPulse } from "_shared/_views/highlights/traverser-stop-highlight";
import { traverserStopClassName } from "_shared/_views/highlights/traverser-stop-scope";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import UploadIcon from "@mui/icons-material/Upload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { useMediaQuery } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { green, lightBlue } from "@mui/material/colors";
import { alpha, styled, useTheme } from "@mui/material/styles";
import { skipToken } from "@reduxjs/toolkit/query";
import { HttpStatusCode } from "axios";
import { Change, diffLines } from "diff";
import {
  createMRTColumnHelper,
  type MRT_Row,
  type MRT_TableInstance,
  type MRT_TableOptions,
} from "material-react-table";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import React, {
  useCallback,
  useMemo,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { defaultStyles, FileIcon } from "react-file-icon";
import { readApiError } from "_shared/_errors/api-error";
import { getApiErrorStatus } from "_shared/_errors/api-error-message";
import {
  showActionableErrorSnackbar,
  showErrorSnackbar,
  type ErrorSnackbarContext,
} from "_shared/_errors/error-snackbar";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { enumKeyOf } from "_shared/_utilities/enum-utils";
import SearchMatch from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { useResizableColumns } from "_shared/_views/tables/table-column-widths";
import Guid from "_shared/guids/guid";
import { createBulkDocumentRequests } from "documents/_api/commands/documents-form-api-request";
import { useCreateDocumentsMutation } from "documents/_api/document-api";
import { getDocumentUploadSnackbar, summarizeDocumentUploadResults } from "documents/_api/document-upload-results";
import { knownLegacyDocumentErrors } from "documents/_errors/document-api-errors";
import { DocumentFileType } from "documents/document-file-type";
import { PROPOSAL_TOPIC_ENTITY_CLASS } from "documents/categories/document-category";
import DocumentTopic from "documents/topics/document-topic";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { useSession } from "users/__session/session-context";
import { NO_UPLOAD_ACCESS_TOOLTIP } from "permissions/access-requests/requestable-access";
import useSessionUser from "permissions/session/use-session-user";
import { AccountType } from "users/account-types/account-type";
import { proposalCollectionItemRevertValue } from "work/proposals/negotiation/proposal-collection-item-revert";
import ProposalFieldHistoryButton from "work/proposals/negotiation/proposal-field-history-button";
import ProposalFieldTimelineDropdown, {
  type ProposalFieldTimelineRevertEntry,
} from "work/proposals/negotiation/proposal-field-timeline-dropdown";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import ProposalInfoTooltip from "work/proposals/_views/proposal-info-tooltip";
import ProposalAddedBadge from "work/proposals/_views/_shared/proposal-added-badge";
import ProposalDeleteActionButton from "work/proposals/_views/_shared/proposal-delete-action-button";
import ProposalInfoLink from "work/proposals/_views/_shared/proposal-info-link";
import ProposalRedlineSubtext from "work/proposals/_views/_shared/proposal-redline-subtext";
import FieldRedline, { FieldRedlineArray } from "work/proposals/redlining/field-redlines/field-redline";
import {
  REMOVED_ROW_REDLINE_ATTRIBUTE,
  removedRowRedlineStyles,
  removedRowRedlineTone,
} from "work/proposals/redlining/field-redlines/_views/removed-row-redline";
import { ReviewerEditLockIcon } from "work/proposals/reviewers/_views/reviewer-edit-lock-feedback";
import { rowActionsColumnWidth } from "work/work-agreements/contracts/_views/document-collection-columns";
import RowActions, { type RowAction } from "_shared/_views/tables/row-actions";
import {
  ADDED_BY_COLUMN_HEADER,
  ADDED_BY_COLUMN_ID,
  ADDED_BY_COLUMN_WIDTH,
  ADDED_COLUMN_HEADER,
  ADDED_COLUMN_ID,
  ADDED_COLUMN_WIDTH,
  DOCUMENT_COLUMN_HEADER,
  DOCUMENT_COLUMN_ID,
  DOCUMENT_COLUMN_WIDTH,
  DOCUMENT_CONTENT_COLUMN_IDS,
  DocumentCollectionActionButton,
  DocumentCollectionHeader,
  DocumentCollectionHeading,
  DocumentCollectionNoRows,
  DocumentCollectionRoot,
  DocumentCollectionToolbar,
  DocumentLockCellContent,
  DocumentRowActions,
  documentCollectionBodyCellStyles,
  documentCollectionHeadCellStyles,
  documentCollectionTableOptions,
  compareDocumentsAdded,
  documentCreatedFullLabel,
  documentCreatedLabel,
  documentCreatedSearchText,
  documentTypeLabel,
  FeeScheduleTableCellText,
  LOCKED_COLUMN_ID,
  LOCKED_COLUMN_WIDTH,
  TYPE_COLUMN_HEADER,
  TYPE_COLUMN_ID,
  TYPE_COLUMN_WIDTH,
  UNKNOWN_VALUE,
  useDocumentCollectionColumnFit,
} from "work/work-agreements/contracts/_views/document-collection-table";
import WorkDocument from "work/work-agreements/contracts/work-document";
import DocumentProcessingState from "documents/classification/_views/document-processing-state";
import { DocumentDownloadName } from "documents/downloads/_views/document-download-control";
import { documentDownloadWithheldReason } from "documents/downloads/document-download-withholding";
import useDocumentDownloadVerdicts from "documents/downloads/use-document-download-verdicts";
import { WorkDocumentType } from "work/work-agreements/contracts/work-document-type";
// #endregion

// #region Column identity
/** The columns a redline decision is drawn across, in the order they are read. */
const REDLINED_COLUMN_IDS = DOCUMENT_CONTENT_COLUMN_IDS;

/**
 * What each of the three collections this one view serves calls itself, and says about itself.
 *
 * The three used to be three near-identical blocks of JSX differing only in these strings, gated on a
 * mixture of `accountType` and `documentsRedline.field`; a Client-owned list whose documents were
 * Conflicts satisfied both gates and drew every row twice. Naming them once, off the single prop that
 * says which collection this is, is what stops that.
 */
const DOCUMENT_COLLECTIONS: Readonly<
  Record<WorkDocumentType, { title: string; about: string; uploadLabel: string }>
> = {
  [WorkDocumentType.Conflicts]: {
    title: "Conflict Documents",
    about:
      "Conflicts document(s) establish potential conflicts checks required by participating clients/vendors",
    uploadLabel: "Upload Conflicts Check Document",
  },
  [WorkDocumentType.ClientPolicy]: {
    title: "Client Policies",
    about: "Client policies establish the terms and conditions that the client requires for the vendor to adhere to",
    // Short, because the Client / Vendor strip above the table already says whose policy it is (#6035).
    uploadLabel: "Upload Policy",
  },
  [WorkDocumentType.VendorPolicy]: {
    title: "Vendor Policies",
    about: "Vendor policies establish the terms and conditions that the vendor requires for the client to adhere to",
    uploadLabel: "Upload Policy",
  },
};

/**
 * The history the collection offers, at the head of its table's view buttons. #4953 took the
 * per-collection Accept All / Reject All / Undo All off all three, so this is the only
 * collection-wide control left.
 *
 * Conflicts names what it covers (#7074). Its history used to sit beside the waiver switch, next to
 * the waiver's own history and with the same icon, so neither said which one it was.
 */
const COLLECTION_HISTORIES: Readonly<Partial<Record<WorkDocumentType, { field: ProposalField; toolTip?: string }>>> = {
  [WorkDocumentType.Conflicts]: {
    field: ProposalField.Conflicts,
    toolTip: "View conflicts documents change history",
  },
  [WorkDocumentType.ClientPolicy]: { field: ProposalField.ClientPolicies },
  [WorkDocumentType.VendorPolicy]: { field: ProposalField.VendorPolicies },
};
// #endregion

/**
 * Which controls a row offers.
 *
 * One answer, read twice: by the row that draws them and by the column that has to be wide enough for
 * them. The conditions cannot live only inside the JSX, because a column sized from a second copy of
 * them starts drifting the moment either copy changes — and the drift is invisible, since a column
 * that is too wide still draws every button.
 */
type DocumentRowControls = {
  replace: boolean;
  remove: boolean;
  undo: boolean;
  /** Accept and Reject, which are offered together. */
  resolve: boolean;
};

const UNDO_FAILURE: ErrorSnackbarContext = { doing: "undoing the changes", operation: "proposal.redline.undo" };
const ACCEPT_FAILURE: ErrorSnackbarContext = { doing: "accepting the changes", operation: "proposal.redline.accept" };
const REJECT_FAILURE: ErrorSnackbarContext = { doing: "rejecting the changes", operation: "proposal.redline.reject" };

/** What an upload refused for its file type says, when the API gave no reason this module knows. */
export const UNSUPPORTED_FILE_MESSAGE = "This type of file can't be uploaded. Choose a different file.";

/**
 * What an upload the API refused for its file type says: the API's reason when this module knows it,
 * else a plain one (#6614). Any other failure is support's to fix, so this says nothing about it.
 *
 * At module scope so the component's upload routine is not asked to compile it. See app/CLAUDE.md.
 */
function unsupportedFileMessage(error: unknown): string | undefined {
  const status = getApiErrorStatus(error);
  if (status !== HttpStatusCode.UnsupportedMediaType && status !== HttpStatusCode.UnprocessableEntity) return undefined;
  const reason = readApiError(error, { knownLegacyText: knownLegacyDocumentErrors });
  return reason.trusted && reason.detail ? reason.detail : UNSUPPORTED_FILE_MESSAGE;
}

function documentRowControls(
  redline: FieldRedline<WorkDocument>,
  // `disableEditing` is optional on this component's own props, and absent reads as editable.
  viewer: { canEditDocuments: boolean; disableEditing?: boolean },
): DocumentRowControls {
  const { canEditDocuments, disableEditing } = viewer;
  // A document somebody else has sent and nobody has decided on yet. It is not the viewer's to
  // dispose of until it is: Accept it or Reject it first, and then do as you like with it.
  const awaitsDecision = redline.isAdded && !redline.isResolved;
  return {
    // Replace is a disposal too — it removes this document and puts another in its place — so it is
    // withheld on the same rows Remove is. Withheld only on Conflicts in practice, the one list both
    // parties hold: on a policy list the counterparty owns nothing, so `canEditDocuments` already
    // said no. A conflicts replace of an undecided incoming document queued an edit the redline
    // could not draw and the save could not resolve (#5549).
    replace: canEditDocuments && !redline.canBeUndone && !awaitsDecision,
    remove: canEditDocuments && !redline.canBeUndone && !redline.isRemoved && !awaitsDecision,
    undo: canEditDocuments && redline.canBeUndone,
    resolve: !disableEditing && !redline.isResolved,
  };
}

/**
 * The controls column on a phone: the history control, and beside it the row's one action or the menu
 * its several fold into — 40px each and the cell's 8px of padding. Sized from what the phone draws
 * rather than from the desktop's row of icons, which left a lone history button in a 192px column.
 */
function phoneRowActionsWidth(controlCount: number): number {
  return (Math.min(controlCount, 1) + 1) * 40 + 8;
}

/** How many of them there are, which is what the column is sized from. */
function documentRowControlCount(controls: DocumentRowControls): number {
  return (
    (controls.replace ? 1 : 0) +
    (controls.remove ? 1 : 0) +
    (controls.undo ? 1 : 0) +
    (controls.resolve ? 2 : 0)
  );
}

// #region Styles
/**
 * The collection's box, flush to the tab's edges.
 *
 * It used to hold everything 16px off them, which left the table's first column and its right edge
 * standing away from the resize divider and the pane while the fee schedule's table met both. The
 * prose takes the inset instead — the collection's heading and the toolbar holding the search and the
 * upload control, both of which `document-collection-table` insets for the draft and active pages alike.
 * See `workPaneProseInset`.
 */
const DocumentsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(2),
  minWidth: "20rem",
  paddingBottom: theme.spacing(2),
}));
const DiffResult = styled("span")<{
  change?: FieldRedline<WorkDocument>;
  disabled?: boolean;
}>(({ change, disabled, theme }) => ({
  backgroundColor: (function () {
    if (disabled) return "none";
    if (change?.isAdded && !change.isResolved) return green[300];
    return "unset";
  })(),
  color: (function () {
    if (change?.isNewlyAdded) return lightBlue[700];
  })(),
  // A bluelined name carries its own underline, in its own color: the link's underline is black.
  textDecoration: (function () {
    if (change?.isRemoved && !change.isResolved) return "line-through";
    if (change?.isNewlyAdded) return "underline";
  })(),
  textDecorationColor: (function () {
    if (change?.isNewlyRemoved) return lightBlue[700];
    if (change?.isRemoved) return theme.palette.error.main;
    return "initial";
  })(),
}));
const ModifiedDiffResult = styled("span")<{
  change?: Change;
  resolved?: boolean;
  accepted?: boolean;
}>(({ change, resolved, accepted, theme }) => ({
  backgroundColor: (function () {
    if (resolved) return "unset";
    if (change?.added) return green[300];
  })(),
  color: (function () {
    if (change?.added && resolved) return lightBlue[700];
    return theme.palette.text.primary;
  })(),
  textDecoration: (function () {
    if (change?.removed) return "line-through";
    if (change?.added && resolved) return "underline";
  })(),
  textDecorationColor: (function () {
    if (accepted) return "unset";
    if (change?.removed && resolved) return lightBlue[700];
    if (change?.removed) return theme.palette.error.main;
    return "initial";
  })(),
  textDecorationThickness: '2px',
  // `diffLines` parts are whole names, so a revised name stacks the struck name above its replacement,
  // the way the value cells beside it do, and each is cut off with an ellipsis rather than the two
  // running together off the end of the cell.
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  '&:hover': {
    textDecorationColor: (function () {
      let color = "initial";
      if (change?.removed && resolved) color = lightBlue[700];
      if (change?.removed && !resolved) color = theme.palette.error.main;
      return `oklch(from ${color} l c h / 0.25)`
    }())
  },
}));
const DocumentLink = styled(ProposalInfoLink, {
  shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled?: boolean }>(({ theme, disabled }) => ({
  color: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
  cursor: disabled ? "default" : "pointer",
  textDecorationColor: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
  pointerEvents: disabled ? "none" : "auto",
  // A flex item of `DocumentName`, allowed to shrink below its text so the name is cut off with an
  // ellipsis instead of pushing the badges out of the cell.
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
}));
/** The download spinner and the name's link, side by side, shrinking to what the badges leave. */
const DocumentName = styled("span")(() => ({
  alignItems: "center",
  display: "flex",
  minWidth: 0,
}));
const ResolvedHelperText = styled("span")(({ theme }) => ({
  color: green[700],
  display: "block",
  fontSize: theme.typography.caption.fontSize,
  lineHeight: 1.4,
}));
/**
 * A value that a redline has added or struck out — the created stamp, the file type, the uploader.
 *
 * Wrapped INSIDE `FeeScheduleTableCellText` rather than around it: the strike-through has to land on the
 * element that directly carries the text, which is what `expectProposalDocumentStruckThrough` and the
 * component spec both read off the computed style.
 */
const DocumentRedlineValue = styled(ProposalRedlineSubtext)(() => ({
  display: "block",
  lineHeight: 1.4,
}));
const DocumentCell = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  minWidth: 0,
  width: "100%",
}));
const DocumentText = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
}));
const DocumentNameLine = styled("span", {
  shouldForwardProp: (prop) => prop !== "added",
})<{ added: boolean }>(({ added, theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(0.75),
  display: "inline-flex",
  maxWidth: "100%",
  minWidth: 0,
  overflow: "hidden",
  paddingRight: theme.spacing(1),
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  // The document name is a Link, which pins its own font weight, so bolding the line alone would leave
  // the name unbolded.
  ...(added && {
    fontWeight: theme.typography.fontWeightBold,
    "& a": { fontWeight: "inherit" },
  }),
}));
const FileTypeIcon = styled("span")(({ theme }) => ({
  display: "inline-flex",
  flexShrink: 0,
  marginRight: theme.spacing(1),
  minWidth: "1.75rem",
  width: "1.75rem",
}));
// #endregion

// #region Types/Props
type DocumentRedlineSelectionProps = {
  className?: string;
  workDocumentType: WorkDocumentType;
  proposalId?: Guid;
  documentsRedline: FieldRedlineArray<WorkDocument>;
  downloadingFileId: Guid | null;
  accountType?: AccountType;
  negotiationId?: string;
  viewerScope?: string;
  disableEditing?: boolean;
  fieldLockTooltip?: string;
  /**
   * False where something above already names the collection — the Policies tab's Client / Vendor
   * strip — so the heading row would only repeat it (#6035).
   */
  showHeading?: boolean;
  activeReviewField: ProposalField | undefined;
  onDownload: (documentId?: Guid) => void;
  onDocumentRedlineChange: (
    redline: FieldRedlineArray<WorkDocument>,
    traversalFieldOverride?: ProposalField | null
  ) => void;
  onBeginReplaceDocument: (documentId: Guid | undefined | null) => void;
  onDocumentClicked: (id: Guid) => void;
  onTraverseToNewField: (fieldInfo: ProposalField | undefined) => void;
};
// #endregion

/**
 * One of the proposal's document collections — Conflict Documents, Client Policies or Vendor Policies —
 * drawn the way the Fee Schedule tab beside it is (#4955), so the Policies and Conflicts tabs read as
 * tables rather than as lists with a border round them.
 *
 * Four accessor columns, each carrying one thing the document HAS: Document (the file's icon and its
 * name, the way Fee Schedule puts a category's identity in its first column), Type, Added by and Added.
 * Every accessor returns the value flattened across the redline's original, revised and current entries,
 * so a search still finds a document by a spelling — or a type, or an uploader — that a redline has
 * struck out. #5027 drew all of this inside ONE column, which is what the rework is for.
 *
 * Everything the redlines themselves say is drawn here and unchanged by that: the `diffLines`
 * strike-through of a replaced document into its replacement, the added/removed colouring, the
 * decided-and-accepted surround and the active-review pulse.
 *
 * A Replace reads the same way from both sides (#5552). Every viewer's row is anchored on the document
 * that was replaced, tied to its replacement by `replacesId`, so every viewer sees one row diffing the
 * old name into the new. A replacement that carries the SAME file name diffs to nothing, so the row
 * also names the replaced document's uploader and date, struck through, and carries a Replaced marker:
 * a re-uploaded version is a change, and a row that looks untouched hides it.
 *
 * Carries the Fee Schedule's whole toolbar — search, Toggle filters, Show/Hide columns, Toggle full
 * screen — and gives every data column MRT's own `⋮` column-actions menu (#4955). These had been
 * suppressed in the name of #4895, which was a misreading of it: what #4895 fixed was a Popover that
 * opened BY ITSELF, when the field-history Prev/Next traversal auto-opened a field's history and marked
 * the rest of the page `aria-hidden` with no user action at all. A menu a reader deliberately clicks
 * open is an ordinary transient dialog — the same thing as the confirm dialogs this collection already
 * raises on Remove — and it gives the page back the moment it is dismissed. The property worth
 * protecting is that NO surface here opens a modal of its own accord and blinds the page; the absence
 * of MRT's toolbar was never that property. `document-redline-selection.spec.tsx` guards the property.
 */
export default function DocumentRedlineSelection(props: Readonly<DocumentRedlineSelectionProps>) {
  const {
    className,
    workDocumentType,
    proposalId,
    documentsRedline,
    downloadingFileId,
    accountType,
    negotiationId,
    viewerScope,
    disableEditing,
    fieldLockTooltip,
    activeReviewField,
    onDownload,
    onDocumentRedlineChange,
    onBeginReplaceDocument,
    onDocumentClicked,
  } = props;
  const showHeading = props.showHeading ?? true;

  const theme = useTheme();
  // Handed the media query as a string rather than as a `(theme) => …` callback: the callback form reads
  // the theme out of `@mui/system`'s own context, which is null wherever this table is rendered without
  // a `ThemeProvider` above it, and the row density is not worth a crash.
  const isMediumDisplaySize = useMediaQuery(theme.breakpoints.down("md"));
  // On a phone a row's controls fold into one menu once there are two of them, as every list page's
  // do, and the history control stays beside it (#5907).
  const phoneQuery = theme.breakpoints.down("sm");
  const isPhone = useMediaQuery(phoneQuery);
  const fileUploaderRef = React.useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [query, setQuery] = useState("");
  // Five columns do not fit the panel the Policies tab gives each collection, and the row-actions
  // column is pinned — so whatever does not fit scrolls underneath the controls, behind the scrollbar
  // `MRTNoMemo` styles on this container. The same container is what a double-clicked column's content
  // is measured in.
  const {
    containerRef: tableContainerRef,
    headCellProps: widthHeadCellProps,
    bodyCellProps: widthBodyCellProps,
  } = useResizableColumns<FieldRedline<WorkDocument>>();
  const showLockColumn = Boolean(disableEditing && fieldLockTooltip);

  const confirm = useConfirmDialog();
  const session = useSession();
  const sessionUser = useSessionUser();
  const [createDocuments] = useCreateDocumentsMutation();

  const collection = DOCUMENT_COLLECTIONS[workDocumentType];
  const collectionHistory = COLLECTION_HISTORIES[workDocumentType];
  const rows = documentsRedline.redlines;
  // A row is the proposal's projection of a document, which carries no verdict of its own. The
  // documents API's copy says whether this reader's download would be refused, and why (#6992).
  // Replace downloads the row's own document, the field's, so its verdict is read too (#7016). It is
  // nearly always the same document, and so the same query.
  const downloadVerdicts = useDocumentDownloadVerdicts(
    rows.flatMap((row) => [downloadableDocumentId(row), row.field.id ?? undefined]),
  );

  /**
   * Whether the viewer OWNS this list, and so may change what is in it.
   *
   * Each party owns its own policies: the server accepts a write to `/clientPolicies/documents` only
   * from the client and `/vendorPolicies/documents` only from the vendor
   * (`ProposalCollaborationAuthorizationService.IsCounterpartyPath`). The upload control already
   * applied that rule; the row and header actions did not — so a vendor could Remove a client policy,
   * confirm a destructive dialog, watch the name strike through and see it listed in the Save
   * confirmation, and then the save failed a bare 403 that took every unrelated change in the same
   * command down with it.
   *
   * This gates OWNERSHIP only. Accept/Reject stay available on the counterparty's list, because that
   * is what negotiating somebody else's terms is: those ride as resolution decisions, which the server
   * accepts from either party (`ProposalResolutionTargetPolicy`). Conflicts documents pass no
   * `accountType` and are edited by whoever holds them, so they are unaffected.
   */
  const viewerOwnsList =
    accountType === undefined ||
    (accountType === AccountType.Vendor) === Boolean(session.context?.viewingAsVendor);
  const canEditDocuments = !disableEditing && viewerOwnsList;

  /**
   * The pinned controls column, sized to the controls THESE rows offer.
   *
   * `ACTIVE_ROW_ACTIONS_COLUMN_WIDTH` is what an active row can come to at its busiest — Replace,
   * Remove, Accept, Reject and the history dropdown — and reserving it whatever the rows hold spent
   * 192px of a 382px phone table on a collection whose one row offered Undo and a history button. The
   * widest row still sets it, so nothing wraps; it is the surface's maximum that is no longer assumed.
   */
  const rowActionsWidth = useMemo(
    () =>
      isPhone
        ? rows.reduce(
          (widest, redline) =>
            Math.max(
              widest,
              phoneRowActionsWidth(
                documentRowControlCount(documentRowControls(redline, { canEditDocuments, disableEditing })),
              ),
            ),
          phoneRowActionsWidth(0),
        )
        : rows.reduce(
        (widest, redline) =>
          Math.max(
            widest,
            rowActionsColumnWidth(
              documentRowControlCount(documentRowControls(redline, { canEditDocuments, disableEditing })),
            ),
          ),
        rowActionsColumnWidth(0),
      ),
    [canEditDocuments, disableEditing, isPhone, rows],
  );

  const [collectionRef, columnFit] = useDocumentCollectionColumnFit({
    showLockColumn,
    rowActionsWidth,
  });

  // #region Added by
  // Who put a document on the proposal is a user id on the document, and a raw GUID is never a thing to
  // show a reader — nor to let a search match on, which is why it reaches neither the cell nor the
  // column's accessor. It is resolved through the same marketplace lookup the Fee Schedule's Team Member
  // column uses, and a document whose uploader cannot be named falls back to a label. A reader the
  // server may not tell who the uploader is gets the uploader's side instead of an id, and that side is
  // the label (#6623).
  const creatorUserIds = useMemo(
    () =>
      new Set(
        rows.flatMap((redline) =>
          documentEntries(redline).flatMap((entry) => (entry?.creatorId ? [entry.creatorId.value] : [])),
        ),
      ),
    [rows],
  );
  const creatorIndividualsQuery = useGetIndividualsByUserIdsQuery(
    creatorUserIds.size > 0 ? { userIds: [...creatorUserIds] } : skipToken,
  );
  const creatorNames = useMemo(
    () =>
      new Map(
        (Individual.fromJSONArray(creatorIndividualsQuery.data, { allowEmpty: true }) ?? []).flatMap(
          (individual) => {
            const fullName = individual.fullName?.trim();
            return fullName ? [[individual.userId.value, fullName] as const] : [];
          },
        ),
      ),
    [creatorIndividualsQuery.data],
  );
  const creatorNameOf = useCallback(
    (entry: WorkDocument | null | undefined): string => {
      if (entry?.creatorId) return creatorNames.get(entry.creatorId.value) ?? UNKNOWN_VALUE;
      return entry?.creatorParty ?? UNKNOWN_VALUE;
    },
    [creatorNames],
  );
  // #endregion

  // #region Row behaviour
  // A revert restores a document's prior value through the SAME `replaceEntryById` edit path any other
  // document change uses, so it records an ordinary redline "Edit" and flows to `queueProposalUpdate`
  // as a `CollectionItemUpdated`/`CollectionItemAdded` op — never a raw `FieldSet`.
  const handleDocumentRevert = useCallback(
    (documentRedline: FieldRedline<WorkDocument>, entry: ProposalFieldTimelineRevertEntry) => {
      if (!documentRedline.field.id) return;
      // This dropdown instance is always in collection-item mode (a document), so `entry.operation` is
      // always CollectionItemAdded/Updated at runtime — narrowed here to satisfy
      // `proposalCollectionItemRevertValue`'s parameter type, which excludes `FieldSet`.
      if (entry.operation?.kind !== "CollectionItemAdded" && entry.operation?.kind !== "CollectionItemUpdated") return;
      const revertValue = proposalCollectionItemRevertValue(entry.operation);
      if (revertValue === undefined) return;
      const revertedDocument = WorkDocument.fromJSON(revertValue as never);
      if (!revertedDocument) return;
      try {
        onDocumentRedlineChange(
          documentsRedline.replaceEntryById(documentRedline.field.id, revertedDocument),
          documentRedline.isNewlyAdded ? null : documentRedline.field,
        );
      } catch (error) {
        console.error("Failed to revert document", error);
        showErrorSnackbar(error, { doing: "reverting the document", operation: "proposal.redline.revert-document" });
      }
    },
    [documentsRedline, onDocumentRedlineChange],
  );

  // Takes the field id the row has already established it has, rather than re-checking it here: a row
  // with no field id offers no Remove at all, so a guard on this side could only ever be dead code.
  const handleDocumentRemoved = useCallback(
    async (fieldId: Guid) => {
      const response = await confirm({
        title: "Remove Document?",
        message: `Do you want to remove this document?`,
        okButtonText: "Remove",
      });
      if (response === ConfirmResponse.Cancel) return;
      try {
        onDocumentRedlineChange?.(documentsRedline.removeEntryByFieldId(fieldId));
      } catch (error) {
        console.error(error);
        showErrorSnackbar(error, { doing: "removing the document", operation: "proposal.redline.remove-document" });
      }
    },
    [confirm, documentsRedline, onDocumentRedlineChange],
  );

  /**
   * A click on the document's name.
   *
   * It downloads, and it also moves the review to this document's field — which the old list got for
   * free by bubbling out of the name and into the row. A table row does not fire for a click that landed
   * on one of its own controls (the name is a link), so the traversal is wired here rather than
   * inherited.
   */
  const handleDocumentNameClicked = useCallback(
    (documentRedline: FieldRedline<WorkDocument>) => {
      onDownload(downloadableDocumentId(documentRedline));
      if (disableEditing) return;
      const fieldId = documentRedline.field.id;
      if (!fieldId) return;
      onDocumentClicked(fieldId);
    },
    [disableEditing, onDocumentClicked, onDownload],
  );

  const getDiffResult = useCallback(
    (documentRedline: FieldRedline<WorkDocument>, table: MRT_TableInstance<FieldRedline<WorkDocument>>) => {
      let previousDocument: WorkDocument | null;
      let revisedDocument: WorkDocument | null;

      if (documentRedline.wasRevised && !documentRedline.isResolved) {
        previousDocument = documentRedline.originalEntry;
        revisedDocument = documentRedline.revisedEntry;
      } else if (documentRedline.isRemoved && documentRedline.isAccepted) {
        previousDocument = documentRedline.originalEntry;
        revisedDocument = null;
      } else {
        previousDocument = documentRedline.revisedEntry;
        revisedDocument = documentRedline.currentEntry ?? null;
      }

      const diff = diffLines(
        previousDocument?.name?.toString() ?? "",
        revisedDocument?.name?.toString() ?? ""
      );

      const previousName = previousDocument?.name?.toString();
      const revisedName = revisedDocument?.name?.toString();
      const title = previousName && revisedName && previousName !== revisedName
        ? `${previousName} → ${revisedName}`
        : (revisedName ?? previousName);

      return (
        <DocumentName>
          {downloadingFileId?.isEqualTo(documentRedline.field.id) && (
            <CircularProgress
              size={16}
              thickness={4}
              color="inherit"
              disableShrink
              sx={{
                color: "text.disabled",
                flexShrink: 0,
                marginRight: 1,
              }}
            />
          )}
          {/* A withheld download leaves the name as text; a click on the row still moves the review here. */}
          <DocumentDownloadName
            document={downloadVerdicts.get(downloadableDocumentId(documentRedline)?.value ?? "")}
            renderLink={(name) => (
              <DocumentLink
                disabled={downloadingFileId?.isEqualTo(documentRedline.field.id)}
                title={title}
                onClick={() => handleDocumentNameClicked(documentRedline)}
              >
                {name}
              </DocumentLink>
            )}
          >
            {diff.map((change: Change) => {
              return (
                <ModifiedDiffResult
                  key={JSON.stringify(change)}
                  change={change}
                  resolved={documentRedline.isResolved}
                  accepted={documentRedline.isAccepted}
                >
                  {/* Each run marks its own match; a term spanning a struck and an added run marks in neither (#6590). */}
                  <SearchMatch text={change.value} table={table} />
                </ModifiedDiffResult>
              );
            })}
          </DocumentDownloadName>
        </DocumentName>
      );
    },
    [downloadVerdicts, downloadingFileId, handleDocumentNameClicked],
  );

  const getUnchangedResult = useCallback(
    (documentRedline: FieldRedline<WorkDocument>, table: MRT_TableInstance<FieldRedline<WorkDocument>>) => {
      const name = documentRedline.currentEntry?.name?.value ??
        documentRedline.revisedEntry?.name?.value ??
        documentRedline.originalEntry?.name?.value;
      return (
        <DocumentName>
          {downloadingFileId?.isEqualTo(documentRedline.field.id) && (
            <CircularProgress
              size={16}
              thickness={4}
              color="inherit"
              disableShrink
              sx={{
                color: "text.disabled",
                flexShrink: 0,
                marginRight: 1,
              }}
            />
          )}
          <DocumentDownloadName
            document={downloadVerdicts.get(downloadableDocumentId(documentRedline)?.value ?? "")}
            renderLink={(children) => (
              <DocumentLink
                disabled={downloadingFileId?.isEqualTo(documentRedline.field.id)}
                title={name}
                onClick={() => handleDocumentNameClicked(documentRedline)}
              >
                {children}
              </DocumentLink>
            )}
          >
            <DiffResult disabled={downloadingFileId?.isEqualTo(documentRedline.field.id)} change={documentRedline}>
              <SearchMatch text={name} table={table} />
            </DiffResult>
          </DocumentDownloadName>
        </DocumentName>
      );
    },
    [downloadVerdicts, downloadingFileId, handleDocumentNameClicked],
  );
  // #endregion

  // #region Cells
  const renderDocumentCell = useCallback(
    (documentRedline: FieldRedline<WorkDocument>, table: MRT_TableInstance<FieldRedline<WorkDocument>>) => {
      const displayed = displayedDocument(documentRedline);
      const extension = enumKeyOf(DocumentFileType, displayed?.fileType).toLocaleLowerCase();
      return (
        <DocumentCell>
          <FileTypeIcon>
            <FileIcon extension={extension} {...defaultStyles[extension as keyof typeof defaultStyles]} />
          </FileTypeIcon>
          <DocumentText>
            {/* `data-document-name` is how a browser test reads a collection's document names off the
                rows (`proposalDocumentNames`): a row also carries the file-type icon, whose SVG spells
                the extension out as real text, the type, the uploader, the upload stamp, an "Added"
                badge and a column of controls, so the row's own text is not the document's name. */}
            <DocumentNameLine
              data-document-name=""
              added={documentRedline.isAdded && documentRedline.isResolved}
            >
              {documentRedline.wasRevised || documentRedline.isRevised
                ? getDiffResult(documentRedline, table)
                : getUnchangedResult(documentRedline, table)}
              {documentRedline.isAdded && documentRedline.isResolved && <ProposalAddedBadge>Added</ProposalAddedBadge>}
              {/* A replacement that keeps the file name of the document it replaces diffs to nothing,
                  so the name alone cannot say that the file changed. The marker says it. */}
              {replacedDocument(documentRedline) && <ProposalAddedBadge>Replaced</ProposalAddedBadge>}
            </DocumentNameLine>
            {isResolvedAccepted(documentRedline) && <ResolvedHelperText>Resolved</ResolvedHelperText>}
            <DocumentProcessingState
              status={downloadVerdicts.get(downloadableDocumentId(documentRedline)?.value ?? "")?.classificationStatus}
              scanStatus={downloadVerdicts.get(downloadableDocumentId(documentRedline)?.value ?? "")?.scanStatus}
            />
          </DocumentText>
        </DocumentCell>
      );
    },
    [downloadVerdicts, getDiffResult, getUnchangedResult],
  );

  /**
   * A value a redline may have added or struck out, on the body2 scale with the measured tooltip.
   *
   * A replacement carries the replaced document's value above its own, struck through, when the two
   * differ — the uploader and the date of a re-uploaded version usually do, and on a row whose two
   * file names are the same they are the only thing that says the document changed.
   */
  const renderRedlineValueCell = useCallback(
    (
      documentRedline: FieldRedline<WorkDocument>,
      table: MRT_TableInstance<FieldRedline<WorkDocument>>,
      value: string,
      previousValue?: string,
      // What tells the two values apart when the text drawn can be the same for both: the Added
      // column draws a short stamp, and two documents added the same day of an earlier year draw
      // one date (#6713).
      valueKey: string = value,
      previousValueKey: string | undefined = previousValue,
    ): ReactNode => {
      const replacedValue = previousValue !== undefined && previousValueKey !== valueKey ? previousValue : undefined;
      // Both values are in the column's accessor, so the search finds either and each marks it (#6590).
      return (
        <FeeScheduleTableCellText title={replacedValue === undefined ? value : `${replacedValue} → ${value}`}>
          {replacedValue !== undefined && (
            <DocumentRedlineValue added={false} removed={true}>
              <SearchMatch text={replacedValue} table={table} />
            </DocumentRedlineValue>
          )}
          <DocumentRedlineValue
            added={documentRedline.isAdded && documentRedline.isResolved}
            removed={documentRedline.isRemoved && documentRedline.isResolved}
          >
            <SearchMatch text={value} table={table} />
          </DocumentRedlineValue>
        </FeeScheduleTableCellText>
      );
    },
    [],
  );
  // #endregion

  // #region Columns
  const columnHelper = useMemo(() => createMRTColumnHelper<FieldRedline<WorkDocument>>(), []);
  const columns = useMemo(
    () => [
      ...(showLockColumn
        ? [
          columnHelper.display({
            header: "",
            id: LOCKED_COLUMN_ID,
            size: LOCKED_COLUMN_WIDTH,
            enableColumnActions: false,
            enableSorting: false,
            muiTableBodyCellProps: { align: "center", sx: { verticalAlign: "middle" } },
            Cell: () => (
              <DocumentLockCellContent>
                <ReviewerEditLockIcon title={fieldLockTooltip} />
              </DocumentLockCellContent>
            ),
          }),
        ]
        : []),
      columnHelper.accessor(documentNameSearchValue, {
        header: DOCUMENT_COLUMN_HEADER,
        id: DOCUMENT_COLUMN_ID,
        size: DOCUMENT_COLUMN_WIDTH,
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: ({ row, table }: { row: MRT_Row<FieldRedline<WorkDocument>>; table: MRT_TableInstance<FieldRedline<WorkDocument>> }) =>
          renderDocumentCell(row.original, table),
      }),
      columnHelper.accessor(documentTypeSearchValue, {
        header: TYPE_COLUMN_HEADER,
        id: TYPE_COLUMN_ID,
        size: TYPE_COLUMN_WIDTH,
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: ({ row, table }: { row: MRT_Row<FieldRedline<WorkDocument>>; table: MRT_TableInstance<FieldRedline<WorkDocument>> }) => {
          const replaced = replacedDocument(row.original);
          return renderRedlineValueCell(
            row.original,
            table,
            documentTypeLabel(displayedDocument(row.original)),
            replaced && documentTypeLabel(replaced),
          );
        },
      }),
      columnHelper.accessor(
        (documentRedline: FieldRedline<WorkDocument>) => documentCreatorSearchValue(documentRedline, creatorNameOf),
        {
          header: ADDED_BY_COLUMN_HEADER,
          id: ADDED_BY_COLUMN_ID,
          size: ADDED_BY_COLUMN_WIDTH,
          enableGlobalFilter: true,
          filterFn: "includesString",
          Cell: ({ row, table }: { row: MRT_Row<FieldRedline<WorkDocument>>; table: MRT_TableInstance<FieldRedline<WorkDocument>> }) => {
            const replaced = replacedDocument(row.original);
            return renderRedlineValueCell(
              row.original,
              table,
              creatorNameOf(displayedDocument(row.original)),
              replaced && creatorNameOf(replaced),
            );
          },
        },
      ),
      columnHelper.accessor(documentCreatedSearchValue, {
        header: ADDED_COLUMN_HEADER,
        id: ADDED_COLUMN_ID,
        size: ADDED_COLUMN_WIDTH,
        enableGlobalFilter: true,
        filterFn: "includesString",
        // By when the document shown was added, not by its text (#6713).
        sortingFn: (left, right) => compareDocumentsAdded(displayedDocument(left.original), displayedDocument(right.original)),
        Cell: ({ row, table }: { row: MRT_Row<FieldRedline<WorkDocument>>; table: MRT_TableInstance<FieldRedline<WorkDocument>> }) => (
          <DocumentAddedRedlineCell documentRedline={row.original} table={table} renderValue={renderRedlineValueCell} />
        ),
      }),
    ],
    [columnHelper, creatorNameOf, fieldLockTooltip, renderDocumentCell, renderRedlineValueCell, showLockColumn],
  );
  // #endregion

  // #region Row actions
  const renderRowActions = useCallback(
    (actionProps: { row: MRT_Row<FieldRedline<WorkDocument>> }) => {
      const redline = actionProps.row.original;
      const controls = documentRowControls(redline, { canEditDocuments, disableEditing });
      // Replace starts by downloading the document it replaces, so it is withheld where that
      // download is, with the same reason (#7016).
      const replaceWithheldReason = documentDownloadWithheldReason(
        downloadVerdicts.get(redline.field.id?.value ?? ""),
      );
      if (isPhone) {
        // The buttons below as data, under exactly the conditions that draw them. A missing id is checked
        // before the `try` rather than thrown inside it: the React Compiler cannot lower a `throw` in a
        // `try` and drops the whole component's memoization when it meets one (app/CLAUDE.md).
        const fieldId = redline.field.id;
        const redlineChange = (failure: ErrorSnackbarContext, change: (id: Guid) => void) => () => {
          if (!fieldId) {
            const missing = new Error("Field ID is missing");
            console.error(failure.operation, missing);
            showErrorSnackbar(missing, failure);
            return;
          }
          try {
            change(fieldId);
          } catch (error) {
            console.error(failure.operation, error);
            showErrorSnackbar(error, failure);
          }
        };
        const menuActions: RowAction[] = [];
        if (controls.replace) {
          menuActions.push({
            key: "replace",
            label: "Replace Document",
            icon: <UploadFileIcon color="success" />,
            disabled: Boolean(replaceWithheldReason),
            disabledReason: replaceWithheldReason,
            onClick: () => onBeginReplaceDocument(redline.field.id),
          });
        }
        if (controls.remove) {
          menuActions.push({
            key: "remove",
            label: "Remove Document",
            icon: <DeleteOutlinedIcon />,
            destructive: true,
            onClick: () => {
              if (redline.currentEntry !== null && redline.field.id) void handleDocumentRemoved(redline.field.id);
            },
          });
        }
        if (controls.undo) {
          menuActions.push({
            key: "undo",
            label: "Undo Changes",
            icon: <UndoIcon color="secondary" />,
            onClick: redlineChange(UNDO_FAILURE, (id) =>
              onDocumentRedlineChange(documentsRedline.undoRedlineById(id), redline.isNewlyAdded ? null : redline.field),
            ),
          });
        }
        if (controls.resolve) {
          menuActions.push(
            {
              key: "accept",
              label: getActionButtonTooltipText(redline.isAdded, redline.isRemoved, true),
              icon: <CheckIcon color="success" />,
              onClick: redlineChange(ACCEPT_FAILURE, (id) =>
                onDocumentRedlineChange(documentsRedline.acceptRedlineById(id)),
              ),
            },
            {
              key: "reject",
              label: getActionButtonTooltipText(redline.isAdded, redline.isRemoved, false),
              icon: <CloseIcon color="error" />,
              onClick: redlineChange(REJECT_FAILURE, (id) =>
                onDocumentRedlineChange(documentsRedline.rejectRedlineById(id)),
              ),
            },
          );
        }
        return (
          <RowActions
            actions={menuActions}
            collapsed={documentRowControlCount(controls) > 1}
            ariaLabel="Document actions"
            trailing={
              canEditDocuments ? (
                <ProposalFieldTimelineDropdown
                  field={redline.field}
                  negotiationId={negotiationId}
                  viewerScope={viewerScope}
                  onRevert={(entry) => handleDocumentRevert(redline, entry)}
                />
              ) : (
                <ProposalFieldHistoryButton field={redline.field} negotiationId={negotiationId} viewerScope={viewerScope} />
              )
            }
          />
        );
      }
      return (
        <DocumentRowActions>
          {controls.replace && (
            // A withheld Replace stays focusable and says it is unavailable, as a withheld Download
            // does: a disabled button cannot be focused, and a keyboard would never reach the reason.
            // describeChild keeps "Replace Document" as the name and makes the reason its description.
            <Tooltip describeChild title={replaceWithheldReason ?? "Replace Document"}>
              <DocumentCollectionActionButton
                aria-label="Replace Document"
                aria-disabled={replaceWithheldReason ? true : undefined}
                disableRipple={Boolean(replaceWithheldReason)}
                sx={replaceWithheldReason ? { cursor: "not-allowed" } : undefined}
                onClick={(event) => {
                  // Stopped even when withheld, so the click does not fall through to the row.
                  event.stopPropagation();
                  if (replaceWithheldReason) return;
                  onBeginReplaceDocument(redline.field.id);
                }}
              >
                <UploadFileIcon color={replaceWithheldReason ? "disabled" : "success"} />
              </DocumentCollectionActionButton>
            </Tooltip>
          )}
          {controls.remove && (
            <Tooltip title="Remove Document">
              <span>
                <ProposalDeleteActionButton
                  aria-label="Remove Document"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (redline.currentEntry !== null && redline.field.id) {
                      void handleDocumentRemoved(redline.field.id);
                    }
                  }}
                />
              </span>
            </Tooltip>
          )}
          {controls.undo && (
            <Tooltip title="Undo Changes">
              <span>
                <DocumentCollectionActionButton
                  onClick={(event) => {
                    event.stopPropagation();
                    try {
                      if (!redline.field.id) throw new Error("Field ID is missing");
                      onDocumentRedlineChange(
                        documentsRedline.undoRedlineById(redline.field.id),
                        redline.isNewlyAdded ? null : redline.field
                      );
                    } catch (error) {
                      console.error("Failed to undo changes", error);
                      showErrorSnackbar(error, { doing: "undoing the changes", operation: "proposal.redline.undo" });
                    }
                  }}
                >
                  <UndoIcon color="secondary" />
                </DocumentCollectionActionButton>
              </span>
            </Tooltip>
          )}
          {/* Resolution, not ownership: accepting or rejecting the other side's proposal is exactly what
              a party does with a list it does not own, and rides as a resolution decision the server
              accepts from either party. */}
          {controls.resolve && (
            <>
              <Tooltip title={getActionButtonTooltipText(redline.isAdded, redline.isRemoved, true)}>
                <span>
                  <DocumentCollectionActionButton
                    onClick={(event) => {
                      event.stopPropagation();
                      try {
                        if (!redline.field.id) throw new Error("Field ID is missing");
                        onDocumentRedlineChange(documentsRedline.acceptRedlineById(redline.field.id));
                      } catch (error) {
                        console.error("Failed to accept changes", error);
                        showErrorSnackbar(error, { doing: "accepting the changes", operation: "proposal.redline.accept" });
                      }
                    }}
                  >
                    <CheckIcon color="success" />
                  </DocumentCollectionActionButton>
                </span>
              </Tooltip>
              <Tooltip title={getActionButtonTooltipText(redline.isAdded, redline.isRemoved, false)}>
                <span>
                  <DocumentCollectionActionButton
                    onClick={(event) => {
                      event.stopPropagation();
                      try {
                        if (!redline.field.id) throw new Error("Field ID is missing");
                        onDocumentRedlineChange(documentsRedline.rejectRedlineById(redline.field.id));
                      } catch (error) {
                        console.error("Failed to reject changes", error);
                        showErrorSnackbar(error, { doing: "rejecting the changes", operation: "proposal.redline.reject" });
                      }
                    }}
                  >
                    <CloseIcon color="error" />
                  </DocumentCollectionActionButton>
                </span>
              </Tooltip>
            </>
          )}
          {/* The dropdown REVERTS, which is an ownership write; the plain button only reads. So the
              counterparty's list gets the read-only history, the same way it loses Replace and Remove. */}
          {canEditDocuments ? (
            <ProposalFieldTimelineDropdown
              field={redline.field}
              negotiationId={negotiationId}
              viewerScope={viewerScope}
              onRevert={(entry) => handleDocumentRevert(redline, entry)}
            />
          ) : (
            <ProposalFieldHistoryButton
              field={redline.field}
              negotiationId={negotiationId}
              viewerScope={viewerScope}
            />
          )}
        </DocumentRowActions>
      );
    },
    [
      canEditDocuments,
      disableEditing,
      documentsRedline,
      downloadVerdicts,
      handleDocumentRemoved,
      handleDocumentRevert,
      isPhone,
      negotiationId,
      onBeginReplaceDocument,
      onDocumentRedlineChange,
      viewerScope,
    ],
  );
  // #endregion

  // #region Table plumbing
  const getRowId = useCallback(
    // The field's id, so a row keeps its identity across renders. A redline with no field id has
    // nothing to act on, but it still has a name worth showing, so it keeps a row under a
    // position-derived id rather than disappearing.
    (row: FieldRedline<WorkDocument>, index: number) => row.field.id?.value ?? `document-${index}`,
    [],
  );

  const muiTableBodyRowProps = useCallback(
    (rowProps: { row: MRT_Row<FieldRedline<WorkDocument>> }) => {
      const redline = rowProps.row.original;
      const fieldId = redline.field.id;
      const isActiveRow = activeReviewField?.id?.isEqualTo(fieldId) ?? false;
      const clickable = Boolean(fieldId) && !disableEditing;
      const removedTone = removedRowRedlineTone(redline);
      return {
        hover: false,
        id: rowProps.row.id,
        [REMOVED_ROW_REDLINE_ATTRIBUTE]: removedTone,
        onClick: (event: ReactMouseEvent<HTMLTableRowElement>) => {
          if (!clickable || !fieldId) return;
          const target = event.target;
          // A click that landed on one of the row's own controls belongs to that control. Without this
          // the row's click handler would fire behind every button in it — and behind the name, which is
          // a link that downloads.
          if (
            target instanceof Element &&
            target.closest(
              "button, a, input, textarea, select, [role='button'], [contenteditable='true'], .MuiFormControl-root, .MuiInputBase-root",
            )
          ) return;
          onDocumentClicked(fieldId);
        },
        // The stop's highlight, and what the page scrolls to — see `traverser-stop-scope`.
        className: traverserStopClassName(isActiveRow),
        sx: {
          cursor: clickable ? "pointer" : "default",
          ...(isActiveRow && { animation: traverserStopRowBorderPulse }),
          ...(removedTone && removedRowRedlineStyles(removedTone)(theme)),
        },
      };
    },
    [activeReviewField, disableEditing, onDocumentClicked, theme],
  );

  const muiTableBodyCellProps = useCallback(
    (cellProps: {
      table: MRT_TableInstance<FieldRedline<WorkDocument>>;
      cell: { column: { id: string } };
      row: MRT_Row<FieldRedline<WorkDocument>>;
    }) => {
      const { table, cell, row } = cellProps;
      const columnId = cell.column.id;
      const isRedlinedColumn = REDLINED_COLUMN_IDS.includes(columnId);
      const isActiveRow = activeReviewField?.id?.isEqualTo(row.original.field.id) ?? false;
      // The green "decided and accepted" surround is drawn across the document's own columns and stops
      // short of the controls, exactly as the Fee Schedule's does. Which rows sit either side of this
      // one is read from the rows the table is actually showing, so a filtered-out neighbour does not
      // leave the surround open at the top or bottom.
      const showResolvedBackground = isRedlinedColumn && isResolvedAccepted(row.original);
      const visibleRows = table.getRowModel().rows;
      const visibleRowIndex = visibleRows.findIndex((visibleRow) => visibleRow.id === row.id);
      const previousRowIsResolvedAccepted = isResolvedAccepted(visibleRows[visibleRowIndex - 1]?.original);
      const nextRowIsResolvedAccepted = isResolvedAccepted(visibleRows[visibleRowIndex + 1]?.original);
      const isFirstRedlinedColumn = columnId === REDLINED_COLUMN_IDS[0];
      const isLastRedlinedColumn = columnId === REDLINED_COLUMN_IDS.at(-1);

      return {
        ...widthBodyCellProps(columnId),
        sx: {
          ...documentCollectionBodyCellStyles(theme, columnId),
          ...(showResolvedBackground && {
            backgroundColor: alpha(green[500], 0.06),
            ...(!previousRowIsResolvedAccepted && { borderTop: `1px solid ${green[500]}` }),
            ...(!nextRowIsResolvedAccepted && { borderBottom: `1px solid ${green[500]}` }),
            ...(isFirstRedlinedColumn && { borderLeft: `1px solid ${green[500]}` }),
            ...(isLastRedlinedColumn && { borderRight: `1px solid ${green[500]}` }),
          }),
          ...(isActiveRow && isRedlinedColumn && {
            borderTop: "2px solid",
            borderBottom: "2px solid",
            ...(isFirstRedlinedColumn && { borderLeft: "2px solid" }),
            ...(isLastRedlinedColumn && { borderRight: "2px solid" }),
            borderColor: "inherit",
            ...traverserStopCellTint,
          }),
        },
      };
    },
    [activeReviewField, theme, widthBodyCellProps],
  );

  const muiTableHeadCellProps = useCallback(
    (headProps: { column: { id: string }; table: MRT_TableInstance<FieldRedline<WorkDocument>> }) => ({
      ...widthHeadCellProps(headProps.column.id, headProps.table),
      sx: documentCollectionHeadCellStyles(theme, headProps.column.id),
    }),
    [theme, widthHeadCellProps],
  );

  const renderEmptyRowsFallback = useCallback(
    ({ table }: { table: MRT_TableInstance<FieldRedline<WorkDocument>> }) => (
      <DocumentCollectionNoRows table={table} filtered={Boolean(query)} />
    ),
    [query],
  );

  // A collection has ONE way of gaining a document — the upload — so this is a single button rather
  // than a split one. It is offered greyed out until the proposal has an id, because that is when the
  // server will take the file. A reader without Read/Write documents is offered it greyed out too,
  // saying why: `Document.CreateAsync` would refuse the file (#6595), as on the draft page.
  const mayUpload = sessionUser?.canUploadDocuments() === true;
  const addActions = useMemo(
    () =>
      canEditDocuments
        ? [
          {
            label: collection.uploadLabel,
            icon: <UploadIcon />,
            loading: isUploading,
            disabled: !proposalId || proposalId.isEmpty || !mayUpload,
            disabledReason: mayUpload ? undefined : NO_UPLOAD_ACCESS_TOOLTIP,
            onClick: () => fileUploaderRef.current?.click(),
          },
        ]
        : [],
    [canEditDocuments, collection.uploadLabel, isUploading, mayUpload, proposalId],
  );

  // The collection's history leads the table's view buttons, on their row and grid (#6035). On the
  // heading row it ended 16px past the fullscreen button below it, and lined up with nothing.
  const renderTopToolbarCustomActions = useCallback(
    ({ table }: { table: MRT_TableInstance<FieldRedline<WorkDocument>> }) => (
      <DocumentCollectionToolbar
        table={table}
        addActions={addActions}
        collectionActions={
          collectionHistory && (
            <ProposalFieldHistoryButton
              field={collectionHistory.field}
              negotiationId={negotiationId}
              viewerScope={viewerScope}
              toolTip={collectionHistory.toolTip}
            />
          )
        }
      />
    ),
    [addActions, collectionHistory, negotiationId, viewerScope],
  );

  // An empty fragment rather than `null`: MRT reads this slot's result with `??`, so a nullish return
  // falls straight back to its own default buttons and the collection would carry two sets.
  const renderToolbarInternalActions = useCallback(() => <></>, []);

  // Everything both document collections ask MRT for comes from the shared shape; what is added here is
  // this page's own rows, cells and row controls.
  const tableOptions: MRT_TableOptions<FieldRedline<WorkDocument>> = useMemo(
    () => ({
      ...documentCollectionTableOptions<FieldRedline<WorkDocument>>({
        tableName: collection.title,
        query,
        onQueryChange: setQuery,
        compactDensity: isMediumDisplaySize,
        tableContainerRef,
        columnFit,
      }),
      columns,
      data: rows as FieldRedline<WorkDocument>[],
      getRowId,
      muiTableBodyRowProps,
      muiTableBodyCellProps,
      muiTableHeadCellProps,
      renderEmptyRowsFallback,
      renderRowActions,
      renderTopToolbarCustomActions,
      renderToolbarInternalActions,
    }),
    [
      collection.title,
      columnFit,
      columns,
      getRowId,
      isMediumDisplaySize,
      muiTableBodyCellProps,
      muiTableBodyRowProps,
      muiTableHeadCellProps,
      query,
      renderEmptyRowsFallback,
      renderRowActions,
      renderToolbarInternalActions,
      renderTopToolbarCustomActions,
      rows,
      tableContainerRef,
    ],
  );
  // #endregion

  async function handleFileUpload(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    if (!event.currentTarget.files) {
      return;
    }
    const files = Array.from(event.currentTarget.files);
    if (files.length === 0) return;
    // Checked before the `try`, so this sentence is shown as the reader's to act on (#6614).
    if (!proposalId || proposalId.isEmpty) {
      event.target.value = "";
      showActionableErrorSnackbar("Save the proposal before you upload documents.");
      return;
    }

    try {
      setIsUploading(true);

      let context: string | undefined;
      let uploadedDocumentType: WorkDocumentType | undefined;
      switch (documentsRedline.field) {
        case ProposalField.ClientPolicies:
          context = "Policy";
          uploadedDocumentType = WorkDocumentType.ClientPolicy;
          break;
        case ProposalField.VendorPolicies:
          context = "Policy";
          uploadedDocumentType = WorkDocumentType.VendorPolicy;
          break;
        case ProposalField.Conflicts:
          context = "Conflicts";
          uploadedDocumentType = WorkDocumentType.Conflicts;
          break;
      }
      if (!uploadedDocumentType) throw new Error("Work Document Type is missing");

      const requests = createBulkDocumentRequests(files, {
        topics: [
          DocumentTopic.create({ context }),
          DocumentTopic.create({ entityClass: PROPOSAL_TOPIC_ENTITY_CLASS, entityId: proposalId }),
        ],
      });
      const results = await createDocuments({ requests }).unwrap();
      const summary = summarizeDocumentUploadResults(results, requests);

      let updatedDocumentsRedline = documentsRedline;
      for (const uploaded of summary.documents) {
        const workDocument = WorkDocument.fromDocument(uploaded, uploadedDocumentType);
        updatedDocumentsRedline = updatedDocumentsRedline.addEntry(workDocument);
      }
      if (summary.uploadedCount > 0) {
        onDocumentRedlineChange?.(updatedDocumentsRedline);
      }

      const snackbar = getDocumentUploadSnackbar(summary);
      enqueueSnackbar(snackbar.message, { variant: snackbar.variant });
    } catch (error) {
      console.error("Failed to upload file", error);

      showErrorSnackbar(error, {
        doing: "uploading the document",
        operation: "proposal.redline.upload-document",
        message: unsupportedFileMessage(error),
      });
    } finally {
      event.target.value = "";
      setIsUploading(false);
    }
  }

  // #4953: no per-collection Accept All / Reject All / Undo All on this collection's header. Every change
  // is decided on the row it belongs to, and the whole-proposal bulk actions on the active proposal page
  // cover "settle everything at once" — a second, collection-scoped copy of them was clutter.

  return (
    <DocumentsContainer className={className}>
      <DocumentCollectionRoot data-document-collection="" ref={collectionRef}>
        {showHeading && (
          <DocumentCollectionHeader data-document-collection-header="">
            {/* The title stays a direct text child of the heading, with the info tooltip beside it rather
                than around it: a wrapper would take the words out of the heading's own text node, and the
                specs read the heading as `getByText(title, { selector: "h3" })`. */}
            <DocumentCollectionHeading>
              {collection.title}
              <ProposalInfoTooltip title={collection.about} placement="bottom" edge="end" />
            </DocumentCollectionHeading>
          </DocumentCollectionHeader>
        )}
        <MRTNoMemo<FieldRedline<WorkDocument>> options={tableOptions} />

      </DocumentCollectionRoot>
      <input type="file" hidden={true} multiple={true} ref={fileUploaderRef} onChange={handleFileUpload} />
    </DocumentsContainer>
  );
}

// #region Row values
// Module-level, so the compiler leaves them alone and so each column's accessor is one named thing
// rather than an inline lambda whose meaning has to be reconstructed from its body.

/** Every version of the document this row stands for, newest reading first. */
/**
 * The Added cell of a document redline: the short stamp of the document shown, and of the one it
 * replaces, struck through (#6713). They are told apart, and named in full on hover, by their whole
 * stamps, because the short form drops the time of an earlier year. It reads the shared minute clock.
 */
function DocumentAddedRedlineCell(props: Readonly<{
  documentRedline: FieldRedline<WorkDocument>;
  table: MRT_TableInstance<FieldRedline<WorkDocument>>;
  renderValue: (
    documentRedline: FieldRedline<WorkDocument>,
    table: MRT_TableInstance<FieldRedline<WorkDocument>>,
    value: string,
    previousValue?: string,
    valueKey?: string,
    previousValueKey?: string,
  ) => ReactNode;
}>) {
  // The table is one mutable object, so a memoized cell would keep the search it first marked.
  "use no memo";

  const now = useMinuteClock();
  const shown = displayedDocument(props.documentRedline);
  const replaced = replacedDocument(props.documentRedline);
  const full = documentCreatedFullLabel(shown);
  const replacedFull = replaced && documentCreatedFullLabel(replaced);
  const title = replacedFull !== undefined && replacedFull !== full ? `${replacedFull} → ${full}` : full;
  return (
    <Tooltip title={title} disableInteractive>
      <span>
        {props.renderValue(
          props.documentRedline,
          props.table,
          documentCreatedLabel(shown, now),
          replaced && documentCreatedLabel(replaced, now),
          full,
          replacedFull,
        )}
      </span>
    </Tooltip>
  );
}

function documentEntries(documentRedline: FieldRedline<WorkDocument>): (WorkDocument | null | undefined)[] {
  return [documentRedline.currentEntry, documentRedline.revisedEntry, documentRedline.originalEntry];
}

/**
 * The document a row's cells describe: where its decisions leave it, or — for a removal — the document
 * that is being removed, which is still what the reader is being asked about.
 */
function displayedDocument(documentRedline: FieldRedline<WorkDocument>): WorkDocument | null | undefined {
  return documentRedline.currentEntry ?? documentRedline.revisedEntry ?? documentRedline.originalEntry;
}

/**
 * The document this row REPLACED, when the document it displays stands in for one.
 *
 * The lineage is `replacesId`, the same rule `FieldRedlineArray` pairs rows by and the same rule the
 * Save summary and the submit dialog use. A row whose displayed document no longer declares the
 * lineage — a replacement the reader rejected, which puts the replaced document back — is not a
 * replacement any more and returns nothing.
 */
function replacedDocument(documentRedline: FieldRedline<WorkDocument>): WorkDocument | undefined {
  const previous = documentRedline.originalEntry;
  const displayed = displayedDocument(documentRedline);
  if (!previous || !displayed) return undefined;
  return displayed.replacesId?.isEqualTo(previous.id) ? previous : undefined;
}

/** Whether this row's change has been decided, and decided in favour of the change. */
function isResolvedAccepted(documentRedline: FieldRedline<WorkDocument> | undefined): boolean {
  return Boolean(
    documentRedline?.wasRevised &&
    documentRedline.isResolved &&
    documentRedline.isAccepted &&
    !documentRedline.isRemoved,
  );
}

function distinctSearchValue(values: (string | undefined)[]): string {
  return [...new Set(values.filter((value): value is string => Boolean(value)))].join(" ");
}

/**
 * Every spelling this document has carried, flattened, so a search over the collection still finds a
 * document by a name a redline has struck out.
 */
function documentNameSearchValue(documentRedline: FieldRedline<WorkDocument>): string {
  return distinctSearchValue(documentEntries(documentRedline).map((entry) => entry?.name?.value));
}

function documentTypeSearchValue(documentRedline: FieldRedline<WorkDocument>): string {
  return distinctSearchValue(
    documentEntries(documentRedline).map((entry) => (entry ? documentTypeLabel(entry) : undefined)),
  );
}

function documentCreatedSearchValue(documentRedline: FieldRedline<WorkDocument>): string {
  return distinctSearchValue(
    documentEntries(documentRedline).map((entry) => (entry ? documentCreatedSearchText(entry) : undefined)),
  );
}

/**
 * What a row's Accept or Reject is named for.
 *
 * The verb used to carry its own trailing space AND the templates added one, so every one of these read
 * "Accept  New Document" with a doubled space — in the tooltip a user sees and in the accessible name a
 * screen reader announces. Invisible to the component tests because Testing Library normalizes
 * whitespace in `getByLabelText`; an exact attribute match, which is what `e2e/support/proposal-ui.ts`
 * uses, does not.
 */
function getActionButtonTooltipText(isAdded: boolean, isRemoved: boolean, accept: boolean): string {
  const verb = accept ? "Accept" : "Reject";

  if (isAdded) return `${verb} New Document`;
  if (isRemoved) return `${verb} Removed Document`;
  return `${verb} All Changes`;
}

/**
 * The uploaders this row has had, by name.
 *
 * A replacement is frequently uploaded by the OTHER party, so the row can honestly carry two — and a
 * search for either must find it. What never reaches this string is the `creatorId` itself: it is a raw
 * GUID, and a GUID is neither a thing to show a reader nor a thing to let them match on by accident.
 */
function documentCreatorSearchValue(
  documentRedline: FieldRedline<WorkDocument>,
  creatorNameOf: (entry: WorkDocument | null | undefined) => string,
): string {
  return distinctSearchValue(documentEntries(documentRedline).map((entry) => (entry ? creatorNameOf(entry) : undefined)));
}

function downloadableDocumentId(documentRedline: FieldRedline<WorkDocument>): Guid | undefined {
  return (
    documentRedline.currentEntry?.id ??
    documentRedline.revisedEntry?.id ??
    documentRedline.originalEntry?.id ??
    undefined
  );
}
// #endregion
